import React, { useState, useEffect, useRef } from 'react';
import { Award, Shield, TrendingUp, Bot, Zap, Settings, Target, CheckCircle, BarChart3, Cog, ArrowRight, Calendar, Star, Users, Clock, DollarSign } from 'lucide-react';
import { Button } from './ui/button';

const ServicesSection = () => {
  const [activeTab, setActiveTab] = useState('process-excellence');
  const [scrollY, setScrollY] = useState(0);
  const sectionRef = useRef(null);
  
  // Parallax scroll effect
  useEffect(() => {
    const handleScroll = () => {
      if (sectionRef.current) {
        const rect = sectionRef.current.getBoundingClientRect();
        const scrollProgress = -rect.top;
        setScrollY(scrollProgress);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial call
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const serviceCategories = {
    'process-excellence': {
      title: 'Process Excellence',
      icon: Award,
      description: 'Six Sigma, Lean, and operational transformation methodologies',
      heroImage: '/process-excellence.png',
      services: [
        {
          icon: Award,
          title: "Six Sigma Implementation",
          description: "DMAIC methodology with 2 BB, 5 GB, and 7 Lean projects completed. Achieve 30-40% efficiency improvements.",
          metrics: "30-40% efficiency gain",
          experience: "21+ years experience",
          caseStudy: "Banking operations transformation: 35% cycle time reduction",
          benefits: ["Reduced defects by 99%", "Improved customer satisfaction", "Cost savings ₹4Cr annually"]
        },
        {
          icon: Zap,
          title: "Lean Process Excellence",
          description: "Lean principles and value stream mapping achieving 25% cycle time reduction and 15% waste elimination.",
          metrics: "25% cycle time reduction",
          experience: "7 lean projects led",
          caseStudy: "Manufacturing workflow optimization: 40% waste reduction",
          benefits: ["Eliminated 8 wastes", "Improved flow efficiency", "Enhanced team productivity"]
        },
        {
          icon: Target,
          title: "Operational Excellence",
          description: "OpEx management across multiple domains with proven track record in offshore transitions and SLA management.",
          metrics: "35% operational efficiency",
          experience: "7 processes transitioned",
          caseStudy: "Multi-site operations standardization: 45% efficiency gain",
          benefits: ["Standardized processes", "Improved KPIs", "Better resource utilization"]
        }
      ]
    },
    'compliance': {
      title: 'Compliance & Risk',
      icon: Shield,
      description: 'ISO certifications, audits, and comprehensive risk management',
      heroImage: 'https://images.unsplash.com/photo-1606206886378-e49a19ad0933',
      services: [
        {
          icon: Shield,
          title: "ISO Certification & Audits",
          description: "ISO 9001:2015, ISO 42001 AIMS, ISO 27001 ISMS Lead Auditor. 50+ internal audits with zero non-conformities.",
          metrics: "50+ audits completed",
          experience: "100% compliance rate",
          caseStudy: "Financial services ISO 27001 implementation: Zero findings",
          benefits: ["100% compliance achieved", "Risk mitigation", "Improved governance"]
        },
        {
          icon: TrendingUp,
          title: "Risk Management & Assessment",
          description: "Comprehensive risk assessment and mitigation across banking, financial services, and technology operations.",
          metrics: "Zero post-transition issues",
          experience: "Multi-domain expertise",
          caseStudy: "Banking risk assessment: ₹16Cr potential loss prevention",
          benefits: ["Risk reduction 80%", "Compliance assurance", "Proactive monitoring"]
        },
        {
          icon: CheckCircle,
          title: "Quality Control & SLA Management",
          description: "Quality monitoring frameworks, SLA development, and compliance assessment across diverse industries.",
          metrics: "95% quality improvement",
          experience: "500+ employees trained",
          caseStudy: "Service quality enhancement: 99.5% SLA achievement",
          benefits: ["Quality metrics improved", "SLA compliance", "Customer satisfaction up"]
        }
      ]
    },
    'automation': {
      title: 'AI & Automation',
      icon: Bot,
      description: 'AI systems, RPA, and intelligent process automation',
      heroImage: 'https://images.unsplash.com/photo-1530825894095-9c184b068fcb',
      services: [
        {
          icon: Bot,
          title: "AI Management Systems",
          description: "AI-powered quality management systems with 40% reduction in manual processes and 99.8% accuracy.",
          metrics: "40% manual reduction",
          experience: "45+ AI/RPA projects",
          caseStudy: "Document processing automation: 90% time reduction",
          benefits: ["99.8% accuracy achieved", "Manual work reduced", "24/7 processing capability"]
        },
        {
          icon: Settings,
          title: "RPA & Process Automation",
          description: "10+ RPA bots deployed, 15+ AI systems integrated. 60% reduction in manual tasks with enterprise-wide impact.",
          metrics: "60% manual task reduction",
          experience: "10+ RPA processes",
          caseStudy: "Invoice processing automation: 85% efficiency gain",
          benefits: ["60% manual reduction", "Error-free processing", "Scalable solutions"]
        },
        {
          icon: BarChart3,
          title: "Statistical Analysis & Data Science",
          description: "Machine learning, NLP with Python, and GenAI foundation for data-driven quality improvements.",
          metrics: "Data-driven insights",
          experience: "Advanced analytics",
          caseStudy: "Predictive analytics implementation: 70% forecast accuracy",
          benefits: ["Advanced analytics", "Predictive insights", "Data-driven decisions"]
        }
      ]
    },
    'training': {
      title: 'Training & Development',
      icon: Users,
      description: 'Professional development and capability building programs',
      services: [
        {
          icon: Cog,
          title: "Six Sigma Training Programs",
          description: "Comprehensive Green Belt and Black Belt training with hands-on project experience and certification support.",
          metrics: "500+ professionals trained",
          experience: "100% success rate",
          caseStudy: "Corporate Six Sigma program: 50 Green Belts certified",
          benefits: ["95% pass rate", "Hands-on projects", "Ongoing mentorship"]
        },
        {
          icon: Users,
          title: "ISO Audit Training",
          description: "Internal auditor training programs for ISO 9001, 27001, and 42001 with practical audit exercises.",
          metrics: "100+ auditors trained",
          experience: "Zero audit findings",
          caseStudy: "ISO auditor development: 30 certified internal auditors",
          benefits: ["Certified auditors", "Practical experience", "Ongoing support"]
        },
        {
          icon: Target,
          title: "Leadership Development",
          description: "Executive coaching and leadership development focused on operational excellence and transformation management.",
          metrics: "50+ leaders coached",
          experience: "Leadership transformation",
          caseStudy: "C-suite transformation coaching: 90% engagement improvement",
          benefits: ["Leadership skills", "Change management", "Strategic thinking"]
        }
      ]
    }
  };

  return (
    <section className="services-section-redesigned" id="services" ref={sectionRef} style={{ position: 'relative', overflow: 'hidden', zIndex: 1 }}>
      {/* Parallax Background Layer - Moves slower */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          transform: `translateY(${scrollY * 0.3}px)`,
          transition: 'transform 0.1s ease-out',
          zIndex: -1
        }}
      >
        <div className="absolute top-0 left-0 w-96 h-96 bg-turquoise-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/3 w-64 h-64 bg-blue-500/5 rounded-full blur-3xl"></div>
      </div>
      
      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header with Creative Left Image */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center mb-16 px-4 lg:px-0">
          {/* Left Side - Hero Image - Parallax Layer 1 (slower movement) */}
          <div 
            className="lg:col-span-5 max-h-[300px] md:max-h-[400px] lg:max-h-none overflow-hidden"
            style={{
              transform: `translateY(${scrollY * 0.15}px)`,
              transition: 'transform 0.1s ease-out'
            }}
          >
            <div className="relative group">
              {/* Main Image Container with Glass Effect */}
              <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-gray-900/50 to-transparent backdrop-blur-sm p-2">
                <img 
                  src="/process-excellence.png"
                  alt="Process Excellence & Technology"
                  className="w-full h-auto rounded-xl transform group-hover:scale-105 transition-transform duration-500"
                />
                {/* Overlay Gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent rounded-xl pointer-events-none"></div>
              </div>
              
              {/* Decorative Elements - Extra slow parallax */}
              <div 
                className="absolute -top-4 -right-4 w-24 h-24 bg-turquoise-500/10 rounded-full blur-2xl"
                style={{
                  transform: `translateY(${scrollY * 0.05}px)`,
                  transition: 'transform 0.1s ease-out'
                }}
              ></div>
              <div 
                className="absolute -bottom-4 -left-4 w-32 h-32 bg-blue-500/10 rounded-full blur-2xl"
                style={{
                  transform: `translateY(${scrollY * 0.08}px)`,
                  transition: 'transform 0.1s ease-out'
                }}
              ></div>
            </div>
          </div>

          {/* Right Side - Header Content - Normal scroll (no parallax) */}
          <div className="lg:col-span-7 text-center lg:text-left">
            <h2 className="services-title mb-6">
              Comprehensive <span className="turquoise-text-gradient">Consulting Services</span>
            </h2>
            <p className="services-subtitle max-w-3xl mb-8">
              From process optimization to digital transformation, we deliver measurable results across four core domains of excellence.
            </p>
            
            {/* Key Metrics - Subtle parallax on each card */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div 
                className="metric-card"
                style={{
                  transform: `translateY(${scrollY * 0.02}px)`,
                  transition: 'transform 0.1s ease-out'
                }}
              >
                <div className="metric-number">60+</div>
                <div className="metric-label">Projects Completed</div>
              </div>
              <div 
                className="metric-card"
                style={{
                  transform: `translateY(${scrollY * 0.03}px)`,
                  transition: 'transform 0.1s ease-out'
                }}
              >
                <div className="metric-number">500+</div>
                <div className="metric-label">Professionals Trained</div>
              </div>
              <div 
                className="metric-card"
                style={{
                  transform: `translateY(${scrollY * 0.025}px)`,
                  transition: 'transform 0.1s ease-out'
                }}
              >
                <div className="metric-number">95%</div>
                <div className="metric-label">Success Rate</div>
              </div>
              <div 
                className="metric-card"
                style={{
                  transform: `translateY(${scrollY * 0.035}px)`,
                  transition: 'transform 0.1s ease-out'
                }}
              >
                <div className="metric-number">21+</div>
                <div className="metric-label">Years Experience</div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabbed Interface */}
        <div className="services-tabs-container">
          {/* Tab Navigation */}
          <div className="services-tabs-nav">
            {Object.entries(serviceCategories).map(([key, category]) => {
              const IconComponent = category.icon;
              return (
                <button
                  key={key}
                  className={`services-tab ${activeTab === key ? 'active' : ''}`}
                  onClick={() => setActiveTab(key)}
                >
                  <div className="services-tab-icon">
                    <IconComponent size={24} />
                  </div>
                  <div className="services-tab-content">
                    <h3 className="services-tab-title">{category.title}</h3>
                    <p className="services-tab-desc">{category.description}</p>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Tab Content */}
          <div className="services-tab-content-area">
            {Object.entries(serviceCategories).map(([key, category]) => (
              <div
                key={key}
                className={`services-tab-panel ${activeTab === key ? 'active' : ''}`}
              >
                {/* Special layout for Process Excellence with side image */}
                {key === 'process-excellence' ? (
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                    {/* Left side - All 3 Cards in One Row */}
                    <div className="lg:col-span-9 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {category.services.map((service, index) => {
                        const ServiceIcon = service.icon;
                        return (
                          <div key={index} className="service-card-enhanced">
                            {/* Service Header */}
                            <div className="service-card-header">
                              <div className="service-icon-container">
                                <ServiceIcon size={28} />
                              </div>
                              <div className="service-header-content">
                                <h3 className="service-title text-base">{service.title}</h3>
                                <div className="service-metrics-badges">
                                  <span className="metric-badge primary text-xs">{service.metrics}</span>
                                </div>
                              </div>
                            </div>

                            {/* Service Content */}
                            <div className="service-card-body">
                              <p className="service-description text-sm">{service.description}</p>
                              
                              {/* Case Study */}
                              <div className="case-study-preview">
                                <div className="case-study-icon">
                                  <Star size={14} />
                                </div>
                                <span className="case-study-text text-xs">{service.caseStudy}</span>
                              </div>

                              {/* Benefits */}
                              <div className="service-benefits">
                                <h4 className="benefits-title text-sm">Key Benefits:</h4>
                                <ul className="benefits-list">
                                  {service.benefits.slice(0, 2).map((benefit, idx) => (
                                    <li key={idx} className="benefit-item text-xs">
                                      <CheckCircle size={12} />
                                      <span>{benefit}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>

                              {/* Action Buttons */}
                              <div className="service-actions flex-col gap-2">
                                <Button 
                                  className="btn-service-primary text-xs py-2"
                                  onClick={() => window.location.href = '/book-consultation'}
                                >
                                  <Calendar size={14} />
                                  Book Consultation
                                </Button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Right side - AI RPA Image - Now visible on all screens */}
                    <div className="lg:col-span-3 flex items-start max-h-[250px] md:max-h-[350px] lg:max-h-none overflow-hidden">
                      <div className="relative group w-full lg:sticky lg:top-8">
                        <img 
                          src="/ai-rpa-automation.png"
                          alt="AI RPA Automation and Risk Management"
                          className="w-full h-auto object-cover rounded-xl transform group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                    </div>
                  </div>
                ) : key === 'compliance' ? (
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                    {/* Left side - Risk Assessment Image - Now visible on all screens */}
                    <div className="lg:col-span-3 flex items-start max-h-[250px] md:max-h-[350px] lg:max-h-none overflow-hidden order-first lg:order-none">
                      <div className="relative group w-full lg:sticky lg:top-8">
                        <img 
                          src="/risk-assessment.png"
                          alt="Risk Assessment and Mitigation"
                          className="w-full h-auto object-cover rounded-xl transform group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                    </div>

                    {/* Right side - All Cards in One Row */}
                    <div className="lg:col-span-9 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {category.services.map((service, index) => {
                        const ServiceIcon = service.icon;
                        return (
                          <div key={index} className="service-card-enhanced">
                            {/* Service Header */}
                            <div className="service-card-header">
                              <div className="service-icon-container">
                                <ServiceIcon size={28} />
                              </div>
                              <div className="service-header-content">
                                <h3 className="service-title text-base">{service.title}</h3>
                                <div className="service-metrics-badges">
                                  <span className="metric-badge primary text-xs">{service.metrics}</span>
                                </div>
                              </div>
                            </div>

                            {/* Service Content */}
                            <div className="service-card-body">
                              <p className="service-description text-sm">{service.description}</p>
                              
                              {/* Case Study */}
                              <div className="case-study-preview">
                                <div className="case-study-icon">
                                  <Star size={14} />
                                </div>
                                <span className="case-study-text text-xs">{service.caseStudy}</span>
                              </div>

                              {/* Benefits */}
                              <div className="service-benefits">
                                <h4 className="benefits-title text-sm">Key Benefits:</h4>
                                <ul className="benefits-list">
                                  {service.benefits.slice(0, 2).map((benefit, idx) => (
                                    <li key={idx} className="benefit-item text-xs">
                                      <CheckCircle size={12} />
                                      <span>{benefit}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>

                              {/* Action Buttons */}
                              <div className="service-actions flex-col gap-2">
                                <Button 
                                  className="btn-service-primary text-xs py-2"
                                  onClick={() => window.location.href = '/book-consultation'}
                                >
                                  <Calendar size={14} />
                                  Book Consultation
                                </Button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : key === 'automation' ? (
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                    {/* Left side - All 3 Cards in One Row */}
                    <div className="lg:col-span-9 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {category.services.map((service, index) => {
                        const ServiceIcon = service.icon;
                        return (
                          <div key={index} className="service-card-enhanced">
                            {/* Service Header */}
                            <div className="service-card-header">
                              <div className="service-icon-container">
                                <ServiceIcon size={28} />
                              </div>
                              <div className="service-header-content">
                                <h3 className="service-title text-base">{service.title}</h3>
                                <div className="service-metrics-badges">
                                  <span className="metric-badge primary text-xs">{service.metrics}</span>
                                </div>
                              </div>
                            </div>

                            {/* Service Content */}
                            <div className="service-card-body">
                              <p className="service-description text-sm">{service.description}</p>
                              
                              {/* Case Study */}
                              <div className="case-study-preview">
                                <div className="case-study-icon">
                                  <Star size={14} />
                                </div>
                                <span className="case-study-text text-xs">{service.caseStudy}</span>
                              </div>

                              {/* Benefits */}
                              <div className="service-benefits">
                                <h4 className="benefits-title text-sm">Key Benefits:</h4>
                                <ul className="benefits-list">
                                  {service.benefits.slice(0, 2).map((benefit, idx) => (
                                    <li key={idx} className="benefit-item text-xs">
                                      <CheckCircle size={12} />
                                      <span>{benefit}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>

                              {/* Action Buttons */}
                              <div className="service-actions flex-col gap-2">
                                <Button 
                                  className="btn-service-primary text-xs py-2"
                                  onClick={() => window.location.href = '/book-consultation'}
                                >
                                  <Calendar size={14} />
                                  Book Consultation
                                </Button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Right side - AI Automation Image - Now visible on all screens */}
                    <div className="lg:col-span-3 flex items-start max-h-[250px] md:max-h-[350px] lg:max-h-none overflow-hidden">
                      <div className="relative group w-full lg:sticky lg:top-8">
                        <img 
                          src="/ai-automation.png"
                          alt="AI Automation"
                          className="w-full h-auto object-cover rounded-xl transform group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                    </div>
                  </div>
                ) : key === 'training' ? (
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                    {/* Left side - Training & Development Image - Now visible on all screens */}
                    <div className="lg:col-span-3 flex items-start max-h-[250px] md:max-h-[350px] lg:max-h-none overflow-hidden order-first lg:order-none">
                      <div className="relative group w-full lg:sticky lg:top-8">
                        <img 
                          src="/training-development.png"
                          alt="Training and Development"
                          className="w-full h-auto object-cover rounded-xl transform group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                    </div>

                    {/* Right side - All 3 Cards in One Row */}
                    <div className="lg:col-span-9 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {category.services.map((service, index) => {
                        const ServiceIcon = service.icon;
                        return (
                          <div key={index} className="service-card-enhanced">
                            {/* Service Header */}
                            <div className="service-card-header">
                              <div className="service-icon-container">
                                <ServiceIcon size={28} />
                              </div>
                              <div className="service-header-content">
                                <h3 className="service-title text-base">{service.title}</h3>
                                <div className="service-metrics-badges">
                                  <span className="metric-badge primary text-xs">{service.metrics}</span>
                                </div>
                              </div>
                            </div>

                            {/* Service Content */}
                            <div className="service-card-body">
                              <p className="service-description text-sm">{service.description}</p>
                              
                              {/* Case Study */}
                              <div className="case-study-preview">
                                <div className="case-study-icon">
                                  <Star size={14} />
                                </div>
                                <span className="case-study-text text-xs">{service.caseStudy}</span>
                              </div>

                              {/* Benefits */}
                              <div className="service-benefits">
                                <h4 className="benefits-title text-sm">Key Benefits:</h4>
                                <ul className="benefits-list">
                                  {service.benefits.slice(0, 2).map((benefit, idx) => (
                                    <li key={idx} className="benefit-item text-xs">
                                      <CheckCircle size={12} />
                                      <span>{benefit}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>

                              {/* Action Buttons */}
                              <div className="service-actions flex-col gap-2">
                                <Button 
                                  className="btn-service-primary text-xs py-2"
                                  onClick={() => window.location.href = '/book-consultation'}
                                >
                                  <Calendar size={14} />
                                  Book Consultation
                                </Button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="services-grid">
                    {category.services.map((service, index) => {
                      const ServiceIcon = service.icon;
                      return (
                        <React.Fragment key={index}>
                          <div className="service-card-enhanced">
                          {/* Service Header */}
                          <div className="service-card-header">
                            <div className="service-icon-container">
                              <ServiceIcon size={32} />
                            </div>
                            <div className="service-header-content">
                              <h3 className="service-title">{service.title}</h3>
                              <div className="service-metrics-badges">
                                <span className="metric-badge primary">{service.metrics}</span>
                                <span className="metric-badge secondary">{service.experience}</span>
                              </div>
                            </div>
                          </div>

                          {/* Service Content */}
                          <div className="service-card-body">
                            <p className="service-description">{service.description}</p>
                            
                            {/* Case Study */}
                            <div className="case-study-preview">
                              <div className="case-study-icon">
                                <Star size={16} />
                              </div>
                              <span className="case-study-text">{service.caseStudy}</span>
                            </div>

                            {/* Benefits */}
                            <div className="service-benefits">
                              <h4 className="benefits-title">Key Benefits:</h4>
                              <ul className="benefits-list">
                                {service.benefits.map((benefit, idx) => (
                                  <li key={idx} className="benefit-item">
                                    <CheckCircle size={14} />
                                    <span>{benefit}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>

                            {/* Action Buttons */}
                            <div className="service-actions">
                            <Button 
                              className="btn-service-primary"
                              onClick={() => window.location.href = '/book-consultation'}
                            >
                              <Calendar size={16} />
                              Book Consultation
                            </Button>
                            <button className="btn-service-secondary">
                              Learn More
                              <ArrowRight size={16} />
                            </button>
                          </div>
                        </div>
                      </div>
                      </React.Fragment>
                    );
                  })}
                </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Call to Action Section */}
        <div className="services-cta-section">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
            {/* Left side - Transform Organization Image - Now visible on all screens */}
            <div className="lg:col-span-4 flex items-start max-h-[300px] md:max-h-[400px] lg:max-h-none overflow-hidden">
              <div className="relative group w-full">
                <img 
                  src="/transform-org.png"
                  alt="Ready to Transform Your Organization"
                  className="w-full h-auto object-cover rounded-xl transform group-hover:scale-105 transition-transform duration-500"
                />
              </div>
            </div>

            {/* Right side - CTA Content */}
            <div className="lg:col-span-8 services-cta-content">
              <h3 className="services-cta-title">Ready to Transform Your Organization?</h3>
              <p className="services-cta-subtitle">
                Get expert guidance tailored to your specific challenges and industry requirements. 
                Our proven methodologies deliver measurable results within 90 days.
              </p>
              
              {/* ROI Calculator Preview */}
              <div className="roi-preview">
                <div className="roi-item">
                  <DollarSign size={20} />
                  <span>Average ROI: 300-400%</span>
                </div>
                <div className="roi-item">
                  <Clock size={20} />
                  <span>Implementation: 30-90 days</span>
                </div>
                <div className="roi-item">
                  <TrendingUp size={20} />
                  <span>Efficiency Gains: 30-60%</span>
                </div>
              </div>

              <div className="services-cta-actions">
                <Button 
                  className="btn-primary-enhanced"
                  onClick={() => window.location.href = '/book-consultation'}
                >
                  <Calendar size={20} />
                  Schedule Free Strategy Call
                  <ArrowRight size={20} />
                </Button>
                
                <Button 
                  className="btn-secondary-enhanced"
                  onClick={() => {
                    const message = `Hi! I'm interested in your ${serviceCategories[activeTab].title} services. Could we discuss my requirements?`;
                    window.open(`https://wa.me/919962536379?text=${encodeURIComponent(message)}`, '_blank');
                  }}
                >
                  <TrendingUp size={20} />
                  WhatsApp Consultation
                  <ArrowRight size={20} />
                </Button>
              </div>

              {/* Trust Indicators */}
              <div className="trust-indicators">
                <div className="trust-item">
                  <Shield size={16} />
                  <span>100% Confidentiality</span>
                </div>
                <div className="trust-item">
                  <Clock size={16} />
                  <span>24hr Response</span>
                </div>
                <div className="trust-item">
                  <Star size={16} />
                  <span>100% Success Rate</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;